/**
 * CreateGroupModal Component
 *
 * Modal for creating a new group within the tenant.
 * Includes group code, group name, description, and optional initial users.
 */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Alert } from '../../../components/ui/Alert';
import { useCreateGroup } from '../hooks/useCreateGroup';
import { useUsers } from '../../users/hooks/useUsers';
import { useAuth } from '../../../contexts/AuthContext';
import { getErrorMessage } from '../../../lib/utils/errors';

/**
 * Generate group code from name
 * Converts "Engineering Team" to "engineering-team"
 */
const generateCodeFromName = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
};

// Validation schema
const createGroupSchema = z.object({
  groupCode: z
    .string()
    .min(3, 'Code must be at least 3 characters')
    .max(50, 'Code must not exceed 50 characters')
    .regex(/^[a-z0-9-]+$/, 'Code must contain only lowercase letters, numbers, and hyphens'),
  groupName: z.string().min(1, 'Group name is required').max(100, 'Group name must not exceed 100 characters'),
  description: z.string().max(500, 'Description must not exceed 500 characters').optional(),
});

type CreateGroupFormData = z.infer<typeof createGroupSchema>;

interface CreateGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const CreateGroupModal: React.FC<CreateGroupModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { tenant } = useAuth();
  const tenantId = tenant?.tenantId || '';
  const createGroupMutation = useCreateGroup(tenantId);
  const { data: users = [] } = useUsers(tenantId);
  const [selectedUsers, setSelectedUsers] = useState<{ userId: string; email: string }[]>([]);
  const [autoGenerateCode, setAutoGenerateCode] = useState<boolean>(true);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm<CreateGroupFormData>({
    resolver: zodResolver(createGroupSchema),
    defaultValues: {
      groupCode: '',
      groupName: '',
      description: '',
    },
  });

  const watchName = watch('groupName');

  // Auto-generate group code from name
  useEffect(() => {
    if (autoGenerateCode && watchName) {
      const generatedCode = generateCodeFromName(watchName);
      setValue('groupCode', generatedCode);
    }
  }, [watchName, autoGenerateCode, setValue]);

  const onSubmit = async (data: CreateGroupFormData) => {
    try {
      await createGroupMutation.mutateAsync({
        ...data,
        users: selectedUsers.length > 0 ? selectedUsers : undefined,
      });
      reset();
      setSelectedUsers([]);
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to create group:', error);
    }
  };

  const handleClose = () => {
    reset();
    setSelectedUsers([]);
    setAutoGenerateCode(true);
    createGroupMutation.reset();
    onClose();
  };

  const toggleUser = (userId: string, email: string) => {
    setSelectedUsers(prev =>
      prev.some(u => u.userId === userId)
        ? prev.filter(u => u.userId !== userId)
        : [...prev, { userId, email }]
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>Create Group</Modal.Header>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <div className="space-y-4">
            {createGroupMutation.isError && (
              <Alert variant="error">
                {getErrorMessage(createGroupMutation.error)}
              </Alert>
            )}

            <div>
              <label htmlFor="groupName" className="block text-sm font-medium text-gray-700">
                Group Name <span className="text-red-500">*</span>
              </label>
              <Input
                id="groupName"
                type="text"
                {...register('groupName')}
                error={errors.groupName?.message}
                placeholder="e.g., Engineering Team"
                className="mt-1"
              />
            </div>

            <div>
              <label htmlFor="groupCode" className="block text-sm font-medium text-gray-700">
                Group Code <span className="text-red-500">*</span>
              </label>
              <Input
                id="groupCode"
                type="text"
                {...register('groupCode')}
                error={errors.groupCode?.message}
                placeholder="e.g., engineering-team"
                className="mt-1"
                disabled={autoGenerateCode}
              />
              <div className="mt-2 flex items-center">
                <Checkbox
                  id="autoGenerateCode"
                  checked={autoGenerateCode}
                  onChange={(e) => setAutoGenerateCode(e.target.checked)}
                />
                <label htmlFor="autoGenerateCode" className="ml-2 text-sm text-gray-600">
                  Auto-generate code from name
                </label>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Code is immutable after creation. Use lowercase letters, numbers, and hyphens.
              </p>
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <Textarea
                id="description"
                {...register('description')}
                error={errors.description?.message}
                placeholder="Optional description of the group"
                rows={3}
                className="mt-1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Initial Users (Optional)
              </label>
              <div className="border border-gray-300 rounded-md max-h-48 overflow-y-auto">
                {users.length === 0 ? (
                  <div className="p-4 text-sm text-gray-500 text-center">
                    No users available
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {users.map((user) => (
                      <label
                        key={user.userId}
                        className="flex items-center p-3 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedUsers.some(u => u.userId === user.userId)}
                          onChange={() => toggleUser(user.userId, user.email)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <span className="ml-3 text-sm text-gray-900">
                          {user.firstName} {user.lastName} ({user.email})
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {selectedUsers.length > 0 && (
                <p className="mt-2 text-sm text-gray-500">
                  {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected
                </p>
              )}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isSubmitting || createGroupMutation.isPending}
          >
            Create Group
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
